import {
  Button,
  Chip,
  Divider,
  Icons,
  iconSize,
  Stack,
  StatusChip,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { taxonomyLabel } from '@munifocus/contracts-gov';
import { useComplaint } from '../lib/files-api';
import { isPlaceholderName } from '../lib/file-display';
import { useIntakeMethods } from '../lib/taxonomies-api';
import { needsSpecify } from './SpecifyField';

/** One reported value. Absent values are dropped rather than shown as "—". */
function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'pre-wrap' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * What was reported, and the way back to correcting it.
 *
 * The file screen showed the property and the owner but never the complaint —
 * so the one thing an officer is most likely to have mistyped was also the one
 * thing they could not see, let alone fix.
 */
export function IntakeSummary({
  fileId,
  complaintId,
  canEdit = true,
}: {
  fileId: string;
  complaintId: string;
  /** False for a read-only seat, which the edit screen's save would refuse. */
  canEdit?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const complaint = useComplaint(complaintId);
  const methods = useIntakeMethods();

  const c = complaint.data;
  if (!c) return null;

  const methodOption = (methods.data ?? []).find((m) => m.key === c.intakeMethod);
  const methodLabel = methodOption ? taxonomyLabel(methodOption, i18n.language) : c.intakeMethod;
  const withOther = (label: string | null, other: string | null, code: string | null) =>
    label && needsSpecify(code ?? '') && other ? `${label} — ${other}` : label;

  return (
    <>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="overline">{t('gov.intake.edit.sectionTitle')}</Typography>
        {canEdit && (
          <Button
            size="small"
            startIcon={<Icons.Pencil size={iconSize.sm} />}
            onClick={() => navigate(`/files/${fileId}/edit`)}
          >
            {t('gov.file.edit.action')}
          </Button>
        )}
      </Stack>

      <Stack spacing={1} sx={{ mt: 1 }} divider={<Divider flexItem />}>
        <Row
          label={t('gov.file.intakeMethod')}
          value={withOther(methodLabel, c.intakeMethodOther, c.intakeMethod)}
        />
        <Row
          label={t('gov.file.complainantName')}
          value={
            isPlaceholderName(c.complainantName)
              ? t(
                  c.source === 'internal'
                    ? 'gov.file.complainantInternal'
                    : 'gov.file.complainantUnnamed',
                )
              : c.complainantName
          }
        />
        <Row label={t('gov.file.complainantEmail')} value={c.complainantEmail} />
        <Row label={t('gov.file.complainantPhone')} value={c.complainantPhone} />
        <Row
          label={t('gov.file.complainantRelationship')}
          value={withOther(
            c.complainantRelationship
              ? t(`gov.complainantRelationship.${c.complainantRelationship}`)
              : null,
            c.complainantRelationshipOther,
            c.complainantRelationship,
          )}
        />
        <Row label={t('gov.file.referringPerson')} value={c.referringPerson} />
        <Row label={t('gov.file.internalContext')} value={c.internalContext} />
        <Row label={t('gov.file.description')} value={c.description} />
      </Stack>

      {/* The flag governs what may leave the file, so it belongs on the file. */}
      {c.anonymous && (
        <StatusChip status="warning" label={t('gov.file.anonymous')} sx={{ mt: 1.5 }} />
      )}
    </>
  );
}
